import type { AgentDetailDto, AgentDto, ProviderDto, Role } from '@agent-platform/shared';
import { hasRole } from '@agent-platform/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty-state';
import { ErrorAlert } from '@/components/error-alert';
import { ListSearch, NoMatches, useListSearch } from '@/components/list-search';
import { PageHeader } from '@/components/page-header';
import { PromptEditor } from '@/components/prompt-editor';
import { RequireTenant } from '@/components/require-tenant';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api, ApiError, errorMessage } from '@/lib/api';
import { AGENT_STATUS_LABELS, formatDateTime, slugify } from '@/lib/format';
import { usePaged } from '@/lib/queries';

export const STATUS_VARIANT = {
  draft: 'outline',
  active: 'secondary',
  archived: 'outline',
} as const;

const NO_PROVIDER = 'none';

/** Creates the agent, then sets its model and starting prompt on the draft in the same step. */
function NewAgentDialog({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [description, setDescription] = useState('');
  const [providerId, setProviderId] = useState<string | null>(null);
  /** null until the admin edits it: then the connection's default model is used. */
  const [model, setModel] = useState<string | null>(null);
  const [prompt, setPrompt] = useState('');

  const providers = usePaged<ProviderDto>(['providers', tenantId], '/providers', open);
  const providerList = providers.data?.pages.flatMap((p) => p.items) ?? [];
  // Preselect a connection that passed its test, with its default model.
  const fallback = providerList.find((p) => p.status === 'ok') ?? providerList[0];
  const chosenId = providerId === NO_PROVIDER ? null : (providerId ?? fallback?.id ?? null);
  const provider = providerList.find((p) => p.id === chosenId);
  const chosenModel = model ?? provider?.defaultModel ?? '';

  const reset = () => {
    setName('');
    setSlug('');
    setSlugEdited(false);
    setDescription('');
    setProviderId(null);
    setModel(null);
    setPrompt('');
  };

  const create = useMutation({
    mutationFn: async () => {
      const agent = await api<AgentDetailDto>('/agents', {
        method: 'POST',
        body: { name, slug, ...(description.trim() ? { description: description.trim() } : {}) },
      });
      const draft = {
        ...(chosenId ? { providerConnectionId: chosenId } : {}),
        ...(chosenId && chosenModel.trim() ? { model: chosenModel.trim() } : {}),
        ...(prompt.trim() ? { systemPrompt: prompt } : {}),
      };
      let draftError: unknown = null;
      if (Object.keys(draft).length > 0) {
        try {
          await api(`/agents/${agent.id}/draft`, { method: 'PATCH', body: draft });
        } catch (error) {
          draftError = error;
        }
      }
      return { agent, draftError };
    },
    onSuccess: ({ agent, draftError }) => {
      if (draftError)
        toast.error(`${agent.name} was created, but its model and prompt were not saved`, {
          description: errorMessage(draftError),
        });
      else toast.success(`${agent.name} created`);
      void queryClient.invalidateQueries({ queryKey: ['agents', tenantId] });
      setOpen(false);
      reset();
      void navigate({ to: '/agents/$agentId', params: { agentId: agent.id } });
    },
  });
  const fieldErr = (path: string) =>
    create.error instanceof ApiError ? create.error.fieldError(path) : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          reset();
          create.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus aria-hidden />
          New agent
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create an agent</DialogTitle>
          <DialogDescription>
            Give it a name, a model and instructions. It starts as a draft you can try in the
            Playground; publish it when it is ready for clients.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-6"
          noValidate
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <ErrorAlert error={create.error} />
          <fieldset className="grid gap-4">
            <legend className="sr-only">About the agent</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid content-start gap-2">
                <Label htmlFor="agent-name">Name</Label>
                <Input
                  id="agent-name"
                  autoComplete="off"
                  required
                  autoFocus
                  placeholder="Support assistant"
                  value={name}
                  aria-invalid={fieldErr('name') ? true : undefined}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!slugEdited) setSlug(slugify(e.target.value));
                  }}
                />
                {fieldErr('name') && <p className="text-destructive text-xs">{fieldErr('name')}</p>}
              </div>
              <div className="grid content-start gap-2">
                <Label htmlFor="agent-slug">URL name</Label>
                <div className="relative">
                  <span
                    aria-hidden
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 font-mono text-sm"
                  >
                    /
                  </span>
                  <Input
                    id="agent-slug"
                    autoComplete="off"
                    required
                    className="pl-5 font-mono"
                    value={slug}
                    aria-invalid={fieldErr('slug') ? true : undefined}
                    aria-describedby="agent-slug-help"
                    onChange={(e) => {
                      setSlug(e.target.value);
                      setSlugEdited(true);
                    }}
                  />
                </div>
                <p
                  id="agent-slug-help"
                  className={
                    fieldErr('slug') ? 'text-destructive text-xs' : 'text-muted-foreground text-xs'
                  }
                >
                  {fieldErr('slug') ??
                    'Filled from the name. Lowercase letters, digits and dashes.'}
                </p>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="agent-description">
                Description <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="agent-description"
                autoComplete="off"
                placeholder="What this agent is for, so teammates can find it"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </fieldset>

          <fieldset className="grid gap-4 border-t pt-5">
            <legend className="float-left mb-1 text-sm font-medium">Model</legend>
            {!providers.isPending && providerList.length === 0 ? (
              <p className="text-muted-foreground clear-left rounded-md border border-dashed p-3 text-sm">
                No model connections yet. Add one in{' '}
                <Link to="/llm-setup" className="underline">
                  LLM setup
                </Link>{' '}
                first, or create the agent now and choose its model later.
              </p>
            ) : (
              <div className="clear-left grid gap-4 sm:grid-cols-2">
                <div className="grid content-start gap-2">
                  <Label htmlFor="new-agent-provider">Provider</Label>
                  <Select
                    value={chosenId ?? NO_PROVIDER}
                    onValueChange={(v) => {
                      // Radix's hidden form <select> reports '' before the options load.
                      if (!v) return;
                      setProviderId(v);
                      setModel(null);
                    }}
                  >
                    <SelectTrigger id="new-agent-provider" className="w-full">
                      <SelectValue placeholder="Choose a provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_PROVIDER}>Choose later</SelectItem>
                      {providerList.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid content-start gap-2">
                  <Label htmlFor="new-agent-model">Model id</Label>
                  <Input
                    id="new-agent-model"
                    list="new-agent-models"
                    autoComplete="off"
                    className="font-mono placeholder:font-sans"
                    disabled={!provider}
                    placeholder={provider?.defaultModel ?? 'Choose a provider first'}
                    value={chosenModel}
                    onChange={(e) => setModel(e.target.value)}
                  />
                  <datalist id="new-agent-models">
                    {provider?.models.map((m) => (
                      <option key={m} value={m} />
                    ))}
                  </datalist>
                </div>
              </div>
            )}
          </fieldset>

          <div className="border-t pt-5">
            <PromptEditor
              id="new-agent-prompt"
              label="System prompt"
              heading={
                <Label htmlFor="new-agent-prompt">
                  System prompt{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
              }
              value={prompt}
              onChange={setPrompt}
              placeholder="You are a helpful assistant for…"
              className="h-44"
            />
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={create.isPending || !name.trim() || !slug.trim()}>
              {create.isPending ? 'Creating…' : 'Create agent'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AgentsContent({ tenantId, role }: { tenantId: string; role: Role }) {
  const canEdit = hasRole(role, 'admin');
  const agents = usePaged<AgentDto>(['agents', tenantId], '/agents');
  const rows = agents.data?.pages.flatMap((p) => p.items) ?? [];
  const search = useListSearch(rows, (a) => [a.name, a.slug, a.description, a.model]);

  return (
    <>
      <PageHeader
        title="Agents"
        description="Agents combine a system prompt with a model. Publish a version to make it available to clients."
        actions={canEdit && <NewAgentDialog tenantId={tenantId} />}
      />
      <ErrorAlert error={agents.error} />
      {!agents.isPending && rows.length === 0 ? (
        <EmptyState title="No agents yet">
          {canEdit
            ? 'Create an agent, choose a provider from LLM setup, and try it in the Playground.'
            : 'An admin has not created any agents yet.'}
        </EmptyState>
      ) : (
        <div>
          <ListSearch
            className="mb-4"
            value={search.query}
            onChange={search.setQuery}
            label="Search agents"
            placeholder="Search by name, slug, description or model"
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Published</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {search.filtered.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <Link
                      to="/agents/$agentId"
                      params={{ agentId: a.id }}
                      className="font-medium hover:underline"
                    >
                      {a.name}
                    </Link>
                    <div className="text-muted-foreground font-mono text-xs">{a.slug}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={STATUS_VARIANT[a.status]}>
                      {AGENT_STATUS_LABELS[a.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>{a.publishedVersion ? `v${a.publishedVersion}` : '—'}</TableCell>
                  <TableCell className="font-mono text-xs">{a.model ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDateTime(a.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length > 0 && search.filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <NoMatches query={search.query} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {agents.hasNextPage && (
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => agents.fetchNextPage()}
          disabled={agents.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </>
  );
}

export function AgentsPage() {
  return (
    <RequireTenant>
      {(tenant) => <AgentsContent tenantId={tenant.id} role={tenant.role} />}
    </RequireTenant>
  );
}
